import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { EquipeRM, EquipeParticipant, EquipeResponse } from '../types';
import { useT } from '../i18n';

function fmtH(h: number): string {
  return h % 1 === 0 ? `${h} h` : `${h.toFixed(1).replace('.', ',')} h`;
}

type Level = 'ok' | 'warn' | 'err' | 'neutral';
function daysLevel(d: number | null): Level { return d === null ? 'neutral' : d <= 15 ? 'err' : d <= 30 ? 'warn' : 'ok'; }
// Hours shown neutrally — no compliance threshold
function presenceLevel(r: number | null): Level { return r === null ? 'neutral' : r >= 80 ? 'ok' : r >= 60 ? 'warn' : 'err'; }
function journalLevel(d: number | null): Level { return d === null ? 'neutral' : d <= 7 ? 'ok' : d <= 14 ? 'warn' : 'err'; }
function levelColor(l: Level): string {
  return l === 'ok' ? 'var(--ok)' : l === 'warn' ? 'var(--warn)' : l === 'err' ? 'var(--err)' : 'var(--ink-3)';
}

/** Determine card alert level: which is the most urgent issue this RM has? */
function cardAlertLevel(rm: EquipeRM, t: (key: string, params?: Record<string, string | number>) => string): { level: Level; reason: string } {
  // Yellow: measures ending within 30 days
  if (rm.ending_soon > 0) {
    return { level: 'warn', reason: t('equipe.alert.endingSoon', { n: rm.ending_soon }) };
  }
  // Yellow: journal very stale
  if (rm.avg_journal_days_stale !== null && rm.avg_journal_days_stale > 21) {
    return { level: 'warn', reason: t('equipe.alert.journalStale', { n: rm.avg_journal_days_stale }) };
  }
  return { level: 'ok', reason: '' };
}

function MetricCell({ value, label, tooltip, color }: { value: string; label: string; tooltip: string; color: string }) {
  return (
    <div title={tooltip} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, cursor: 'help' }}>
      <span style={{ fontWeight: 700, fontSize: 'var(--t-14)', color }}>{value}</span>
      <span style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)', fontWeight: 500 }}>{label}</span>
    </div>
  );
}

function RMCard({ rm }: { rm: EquipeRM }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const alert = cardAlertLevel(rm, t);
  const borderColor = alert.level === 'err' ? 'var(--err)' : alert.level === 'warn' ? 'var(--warn)' : 'var(--line)';
  const hasAlert = alert.level !== 'ok';

  return (
    <div style={{
      border: '1px solid var(--line)',
      borderLeft: hasAlert ? `3px solid ${borderColor}` : '1px solid var(--line)',
      borderRadius: 'var(--r1)',
      backgroundColor: 'var(--surface)',
      padding: 'var(--s12) var(--s16)',
    }}>
      {/* Header: name + participant count + programmes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s8)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 'var(--t-14)' }}>{rm.last_name} {rm.first_name}</span>
        <span className="stat-pill">{t('equipe.participantCount', { n: rm.active_participants })}</span>
        {rm.programmes.map(prog => (
          <span key={prog} className="badge badge-sm">{prog}</span>
        ))}
      </div>

      {/* Alert reason (if any) */}
      {hasAlert && (
        <div style={{
          fontSize: 'var(--t-11)', fontWeight: 500, marginBottom: 'var(--s8)',
          color: alert.level === 'err' ? 'var(--err)' : 'var(--warn)',
        }}>
          ⚠ {alert.reason}
        </div>
      )}

      {/* Metrics row */}
      <div style={{ display: 'flex', gap: 'var(--s12)', flexWrap: 'wrap', marginBottom: 'var(--s8)' }}>
        <MetricCell
          value={rm.avg_hours_per_week !== null ? fmtH(rm.avg_hours_per_week) : '—'}
          label={t('equipe.metric.avgActivity')}
          tooltip={t('equipe.metric.avgActivityTooltip')}
          color="var(--ink-1)"
        />
        <MetricCell
          value={rm.avg_presence_rate !== null ? `${rm.avg_presence_rate} %` : '—'}
          label={t('equipe.metric.avgPresence')}
          tooltip={t('equipe.metric.avgPresenceTooltip')}
          color={levelColor(presenceLevel(rm.avg_presence_rate))}
        />
        <MetricCell
          value={rm.avg_journal_days_stale !== null ? `${rm.avg_journal_days_stale}` : '—'}
          label={t('equipe.metric.daysNoJournal')}
          tooltip={t('equipe.metric.daysNoJournalTooltip')}
          color={levelColor(journalLevel(rm.avg_journal_days_stale))}
        />
        <MetricCell
          value={`${rm.ending_soon}`}
          label={t('equipe.metric.endingSoon')}
          tooltip={t('equipe.metric.endingSoonTooltip')}
          color={rm.ending_soon > 0 ? 'var(--warn)' : 'var(--ink-3)'}
        />
      </div>

      {/* Expandable participant list */}
      {rm.participants.length > 0 && (
        <div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setExpanded(!expanded)}
            style={{ fontSize: 'var(--t-11)', padding: '2px 8px' }}
          >
            {expanded ? t('equipe.toggleHide') : t('equipe.toggleShow')} ({rm.participants.length})
          </button>

          {expanded && (
            <table className="table table-compact" style={{ marginTop: 'var(--s4)' }}>
              <thead>
                <tr>
                  <th>{t('equipe.col.participant')}</th>
                  <th>{t('equipe.col.contract')}</th>
                  <th>{t('equipe.col.mandant')}</th>
                  <th style={{ textAlign: 'center' }}>{t('equipe.col.daysRemaining')}</th>
                  <th style={{ textAlign: 'center' }}>{t('equipe.col.hoursPerWeek')}</th>
                </tr>
              </thead>
              <tbody>
                {rm.participants.map((p: EquipeParticipant, i: number) => (
                  <tr
                    key={p.participant_id}
                    className="table-row-clickable"
                    style={{ cursor: 'pointer', backgroundColor: i % 2 === 1 ? 'var(--accent-dim)' : 'var(--surface)' }}
                    onClick={() => { window.location.hash = `participant?id=${p.participant_id}`; }}
                  >
                    <td style={{ fontWeight: 500 }}>{p.last_name} {p.first_name}</td>
                    <td><span className="badge badge-sm">{p.programme_code}</span></td>
                    <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-2)' }}>{p.mandant_name || '—'}</td>
                    <td style={{ textAlign: 'center', fontWeight: 500, color: levelColor(daysLevel(p.days_remaining)) }}>
                      {p.days_remaining !== null ? p.days_remaining : '—'}
                    </td>
                    <td style={{ textAlign: 'center', color: 'var(--ink-1)' }}>
                      {p.hours_per_week != null ? fmtH(p.hours_per_week) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export function EquipePage() {
  const { t } = useT();
  const [rms, setRms] = useState<EquipeRM[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getEquipe() as EquipeResponse;
      setRms(result.rms || []);
    } catch (err) {
      console.error('Failed to load equipe:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalParticipants = rms.reduce((sum, rm) => sum + rm.active_participants, 0);
  const totalEndingSoon = rms.reduce((sum, rm) => sum + rm.ending_soon, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'var(--t-17)', fontWeight: 700 }}>{t('equipe.title')}</h1>
        <span className="stat-pill">{t('equipe.referentCount', { n: rms.length })}</span>
        <span className="stat-pill">{t('equipe.participantCount', { n: totalParticipants })}</span>
        {totalEndingSoon > 0 && (
          <span className="stat-pill" style={{ color: 'var(--warn)' }}>{t('equipe.endingSoonCount', { n: totalEndingSoon })}</span>
        )}
      </div>

      <p style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginBottom: 'var(--s12)' }}>
        {t('equipe.description')}
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>
      ) : rms.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', textAlign: 'center', padding: 'var(--s32)' }}>{t('equipe.noReferents')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
          {rms.map(rm => (
            <RMCard key={rm.staff_id} rm={rm} />
          ))}
        </div>
      )}
    </div>
  );
}
